import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const ResultPage = ({ user }) => {
  const { attemptId } = useParams();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('course_id');
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if data was passed via location.state (old flow)
    if (location.state?.result) {
      setResult(location.state.result);
      setTest(location.state.test);
      setQuestions(location.state.questions || []);
      setLoading(false);
    } else if (attemptId) {
      // Fetch result data using attemptId
      fetchResult();
    } else {
      setLoading(false);
    }
  }, [attemptId]);

  // Prevent swipe back to test page after completing test
  useEffect(() => {
    const handlePopState = (e) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to history
    window.history.pushState(null, '', window.location.pathname);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`/test-attempts/${attemptId}`);
      setResult(response.data);
      setTest(response.data.test);
      setQuestions(response.data.questions || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load result:', error);
      toast.error('Failed to load result');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="text-xl font-semibold text-blue-600">Loading result...</div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">No result data available</p>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { score, total_questions, correct_answers = {}, user_answers = {} } = result;
  
  // Add safety checks
  if (score === undefined || total_questions === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">Invalid result data</p>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const percentage = ((score / total_questions) * 100).toFixed(2);
  
  // Case-insensitive comparison for correct/wrong counts
  const correctCount = Object.keys(correct_answers).filter(
    (qId) => user_answers[qId] && correct_answers[qId]?.toUpperCase() === user_answers[qId]?.toUpperCase()
  ).length;
  const wrongCount = Object.keys(user_answers).filter(
    (qId) => user_answers[qId] && correct_answers[qId]?.toUpperCase() !== user_answers[qId]?.toUpperCase()
  ).length;
  const skippedCount = total_questions - Object.keys(user_answers).filter(qId => user_answers[qId]).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-4 sm:py-12 px-3 sm:px-4" data-testid="result-page">
      <div className="max-w-4xl mx-auto">
        {/* Score Card */}
        <Card className="mb-4 sm:mb-8 shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6">
            <CardTitle className="text-center text-xl sm:text-3xl">Test Result</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 sm:pt-8 px-3 sm:px-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="text-4xl sm:text-6xl font-bold text-blue-600 mb-1 sm:mb-2" data-testid="score-display">
                {score.toFixed(2)}
              </div>
              <div className="text-base sm:text-xl text-gray-600">out of {total_questions}</div>
              <div className="text-xl sm:text-2xl font-semibold text-blue-700 mt-1 sm:mt-2" data-testid="percentage-display">
                {percentage}%
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-8">
              <div className="text-center p-2 sm:p-4 bg-green-50 rounded-lg">
                <div className="text-xl sm:text-3xl font-bold text-green-600" data-testid="correct-count">{correctCount}</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1">Correct</div>
              </div>
              <div className="text-center p-2 sm:p-4 bg-red-50 rounded-lg">
                <div className="text-xl sm:text-3xl font-bold text-red-600" data-testid="wrong-count">{wrongCount}</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1">Wrong</div>
              </div>
              <div className="text-center p-2 sm:p-4 bg-gray-50 rounded-lg">
                <div className="text-xl sm:text-3xl font-bold text-gray-600" data-testid="skipped-count">{skippedCount}</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1">Skipped</div>
              </div>
            </div>

            <div className="flex justify-center">
              <Button
                data-testid="back-dashboard-button"
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
              >
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Answers */}
        <Card className="shadow-lg">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl">Answer Key</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="space-y-4 sm:space-y-6">
              {questions?.map((question, index) => {
                const userAnswer = user_answers[question.id];
                const correctAnswer = correct_answers[question.id];
                // Case-insensitive comparison
                const isCorrect = userAnswer && correctAnswer && userAnswer.toUpperCase() === correctAnswer.toUpperCase();
                const isSkipped = !userAnswer;

                return (
                  <div
                    key={question.id}
                    className={`p-3 sm:p-4 rounded-lg border-2 ${
                      isSkipped
                        ? 'border-gray-300 bg-gray-50'
                        : isCorrect
                        ? 'border-green-300 bg-green-50'
                        : 'border-red-300 bg-red-50'
                    }`}
                    data-testid={`answer-item-${question.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
                      <p className="font-semibold text-gray-800 text-sm sm:text-base">
                        Q{index + 1}. {question.question_text}
                      </p>
                      <span
                        className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-semibold self-start ${
                          isSkipped
                            ? 'bg-gray-200 text-gray-700'
                            : isCorrect
                            ? 'bg-green-200 text-green-700'
                            : 'bg-red-200 text-red-700'
                        }`}
                      >
                        {isSkipped ? 'Skipped' : isCorrect ? 'Correct' : 'Wrong'}
                      </span>
                    </div>

                    {/* Display based on question type */}
                    {question.question_type === 'combo' ? (
                      /* Statement-Combination MCQ */
                      <div className="space-y-3">
                        {/* Statements */}
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r">
                          <p className="text-xs font-semibold text-blue-900 mb-2">Statements:</p>
                          <div className="space-y-1.5 text-sm">
                            <div><strong>A.</strong> {question.stmt_a}</div>
                            <div><strong>B.</strong> {question.stmt_b}</div>
                            <div><strong>C.</strong> {question.stmt_c}</div>
                            <div><strong>D.</strong> {question.stmt_d}</div>
                          </div>
                        </div>

                        {/* Combination Options */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">Combination Options:</p>
                          <div className="space-y-2 text-sm">
                            {['a', 'b', 'c', 'd'].map((option) => {
                              const isUserAnswer = userAnswer && userAnswer.toUpperCase() === option.toUpperCase();
                              const isCorrectOption = correctAnswer && correctAnswer.toUpperCase() === option.toUpperCase();
                              const optionText = question[`combo_option_${option}`];
                              
                              if (!optionText) return null;

                              return (
                                <div
                                  key={option}
                                  className={`p-2 rounded ${
                                    isCorrectOption
                                      ? 'bg-green-100 font-semibold border border-green-300'
                                      : isUserAnswer
                                      ? 'bg-red-100 border border-red-300'
                                      : 'bg-gray-50'
                                  }`}
                                >
                                  <span className="font-semibold mr-2">({option})</span>
                                  {optionText}
                                  {isCorrectOption && (
                                    <span className="ml-2 text-green-600 font-semibold">✓ Correct Answer</span>
                                  )}
                                  {isUserAnswer && !isCorrectOption && (
                                    <span className="ml-2 text-red-600 font-semibold">✗ Your Answer</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Simple MCQ */
                      <div className="space-y-2 text-sm">
                        {['a', 'b', 'c', 'd'].map((option) => {
                          const isUserAnswer = userAnswer && userAnswer.toUpperCase() === option.toUpperCase();
                          const isCorrectOption = correctAnswer && correctAnswer.toUpperCase() === option.toUpperCase();
                          const optionText = question[`option_${option}`];
                          
                          if (!optionText) return null;

                          return (
                            <div
                              key={option}
                              className={`p-2 rounded ${
                                isCorrectOption
                                  ? 'bg-green-100 font-semibold'
                                  : isUserAnswer
                                  ? 'bg-red-100'
                                  : ''
                              }`}
                            >
                              <span className="font-semibold mr-2">{option.toUpperCase()}.</span>
                              {optionText}
                              {isCorrectOption && (
                                <span className="ml-2 text-green-600">✓ Correct Answer</span>
                              )}
                              {isUserAnswer && !isCorrectOption && (
                                <span className="ml-2 text-red-600">✗ Your Answer</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Explanation */}
                    {question.explanation && (
                      <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                        <p className="text-xs font-semibold text-blue-800 mb-1">Explanation:</p>
                        <p className="text-sm text-gray-700">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResultPage;